import React, { useState, useLayoutEffect } from "react";
import * as Highcharts from "highcharts";
import Boost from "highcharts/modules/boost";
import Exporting from "highcharts/modules/exporting";

import { HighchartsReactWithCallbackFix } from "./HighchartsReactWithCallbackFix";

Boost(Highcharts);
Exporting(Highcharts);

export const HighchartsGrid = ({
  options: allOptions,
  viewAsGrid,
  syncZoomAndCursor = true,
}: {
  options: Highcharts.Options[];
  viewAsGrid: boolean;
  syncZoomAndCursor?: boolean;
}) => {
  if (syncZoomAndCursor) {
    Highcharts.Pointer.prototype.reset = function () {
      return undefined;
    };
  }

  const [toggleIndex, setToggle] = useState<{ [key: number]: boolean }>({});
  const [autoToggleIndex, setAutoToggle] = useState<{ [key: number]: boolean }>(
    {}
  );

  const [seriesIndex] = useState<{
    [chartIdx: number]: { [seriesName: string]: boolean };
  }>({});

  // inspired by official sample here
  // https://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/master/samples/highcharts/demo/synchronized-charts/
  const onMove = !syncZoomAndCursor
    ? () => {
        /* */
      }
    : (
        e:
          | React.MouseEvent<HTMLElement>
          | React.PointerEvent<HTMLElement>
          | React.TouchEvent<HTMLElement>
      ) => {
        const highchartsIndexAttribute = (e.currentTarget.childNodes.item(
          0
        ) as HTMLElement).getAttribute("data-highcharts-chart");
        if (!highchartsIndexAttribute) return;
        const highchartsIndex = parseInt(highchartsIndexAttribute);

        const thisChart = Highcharts.charts[highchartsIndex];
        if (!thisChart?.pointer) return;
        const event = thisChart.pointer.normalize(e.nativeEvent);

        Highcharts.each(Highcharts.charts, function (chart: Highcharts.Chart) {
          if (!chart || chart === thisChart) return;
          // show the pointer on all the charts
          if (chart?.pointer) {
            // Highcharts type definitions don't include these
            // https://github.com/highcharts/highcharts/issues/12532
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const point: any = (chart.pointer as any).findNearestKDPoint(
              chart.series,
              false,
              event
            );

            if (
              point &&
              chart.series.some((series) => series.visible === true)
            ) {
              point.onMouseOver(); // Show the hover marker
            }
          }
        });
      };

  const syncExtremes: Highcharts.AxisSetExtremesEventCallbackFunction = function syncExtremes(
    e
  ) {
    const thisChart = this.chart;
    if (e.trigger !== "syncExtremes") {
      // Prevent feedback loop
      Highcharts.each(Highcharts.charts, function (chart: Highcharts.Chart) {
        if (!chart || chart === thisChart) return;
        chart.xAxis[0]?.setExtremes &&
          chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
            trigger: "syncExtremes",
          });
      });
    }
  };
  useLayoutEffect(
    () => Highcharts.charts.forEach((chart) => chart?.reflow()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewAsGrid]
  );

  const optionsWithSyncExtremes = syncZoomAndCursor
    ? allOptions.map(
        (options) =>
          ({
            ...options,
            xAxis: {
              ...options.xAxis,
              events: {
                ...(options?.xAxis as Highcharts.XAxisOptions)?.events,
                setExtremes: syncExtremes,
              },
            },
          } as Highcharts.Options)
      )
    : allOptions;

  const handleDisableAutoScale = (index: number) => {
    const chart = allOptions[index];

    // handle a show/hide when no individual series have been selected
    if (seriesIndex[index] === undefined) {
      seriesIndex[index] = {};
      if (chart.series) {
        for (const serie of chart.series) {
          if (serie.name) {
            seriesIndex[index][serie.name] = true;
          }
        }
      }
    }

    if (
      (chart.yAxis as Highcharts.YAxisOptions[]) &&
      Object.values(seriesIndex[index]).includes(true)
    ) {
      const yAxis = chart.yAxis as Highcharts.YAxisOptions[];
      if (yAxis.length === 1) {
        const highcharts = Highcharts;
        const min = highcharts.charts[index]?.yAxis[0].min;
        const max = highcharts.charts[index]?.yAxis[0].max;

        if (min !== undefined && max !== undefined) {
          yAxis[0].min = !autoToggleIndex[index] ? min : null;
          yAxis[0].max = !autoToggleIndex[index] ? max : null;
          yAxis[0].alignTicks = !autoToggleIndex[index] ? false : true;
          setAutoToggle({
            ...autoToggleIndex,
            [index]: !autoToggleIndex[index],
          });
        }
      }
    }
  };

  const handleHideShowToggle = (
    toggleIndex: { [key: number]: boolean },
    index: number
  ) => {
    const charts = allOptions;
    const chart = charts[index];
    const series = chart.series as Highcharts.SeriesOptionsType[];
    const seriesAltered = series.map((x, i) => ({
      ...x,
      visible: toggleIndex[index],
    }));
    allOptions[index].series = seriesAltered;

    if (seriesIndex[index] === undefined) {
      seriesIndex[index] = {};
    }
    series.forEach((serie) => {
      if (serie.name !== undefined) {
        seriesIndex[index][serie.name] = toggleIndex[index];
      }
    });
    setToggle({ ...toggleIndex, [index]: !toggleIndex[index] });
  };

  const handleTogglePopulation = (options: Highcharts.Options[]) => {
    if (options.length !== Object.keys(toggleIndex).length) {
      const charts = options;
      const insert = charts
        .map((_, i) => i)
        .reduce((arr, v) => ({ ...arr, [v]: false }), {});
      const toggleIndexInit: { [key: number]: boolean } = {};
      const newToggleIndexInit = { ...toggleIndexInit, ...insert };
      setToggle(newToggleIndexInit);
      setAutoToggle(newToggleIndexInit);
    }

    const charts = options;
    charts.forEach((chart, index) => {
      if (chart.series !== undefined) {
        for (const serie of chart.series) {
          serie.events = {
            legendItemClick: function (e) {
              if (seriesIndex[e.target.chart.index] === undefined) {
                seriesIndex[e.target.chart.index] = {};
              }
              seriesIndex[e.target.chart.index][e.target.name] = !e.target
                .visible;
            },
          };
        }
      }
    });
    allOptions.forEach((chart, chartIdx) => {
      chart.series?.forEach((serie) => {
        if (serie.name !== undefined) {
          if (
            seriesIndex &&
            seriesIndex[chartIdx] !== undefined &&
            seriesIndex[chartIdx][serie.name] !== undefined
          )
            serie.visible = seriesIndex[chartIdx][serie.name];
        }
      });
      if (chart.exporting?.buttons) {
        const customButton1 = {
          x: -90,
          onclick: function () {
            handleDisableAutoScale(chartIdx);
          },
          text:
            autoToggleIndex[chartIdx] === false
              ? "AutoScaling Y-axis: ON"
              : "AutoScaling Y-axis: OFF",
          theme: {
            "stroke-width": 1,
            stroke: "silver",
            r: 0,
          },
        };

        const customButton2 = {
          x: toggleIndex[chartIdx] === false ? -30 : -26,
          onclick: function () {
            handleHideShowToggle(toggleIndex, chartIdx);
          },
          text: toggleIndex[chartIdx] === false ? "Hide All" : "Show All",
          theme: {
            "stroke-width": 1,
            stroke: "silver",
            r: 0,
          },
        };
        // if we want custom buttons we have to ignore the type errors.
        // @ts-ignore:
        // eslint-disable-next-line
        chart.exporting.buttons.customButton1 = customButton1;
        // @ts-ignore:
        // eslint-disable-next-line
        chart.exporting.buttons.customButton2 = customButton2;
      }
    });
  };

  return (
    <div className="flex flex-wrap -mx-2">
      {handleTogglePopulation(optionsWithSyncExtremes)}
      {optionsWithSyncExtremes.map((options, index) => (
        <div className={`${viewAsGrid ? "w-1/2" : "w-full"} px-2`} key={index}>
          <div
            className="rounded-lg shadow-lg overflow-hidden mb-6"
            onMouseMove={onMove}
            onTouchMove={onMove}
            onTouchStart={onMove}
          >
            <HighchartsReactWithCallbackFix
              highcharts={Highcharts}
              options={options}
            />
          </div>
          <br></br>
        </div>
      ))}
    </div>
  );
};
